import { memo, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { usePage } from "@inertiajs/react";
import classNames from "classnames";

import VimeoPlayer from "@vimeo/player";
import * as iframeApiLoader from "@kinescope/player-iframe-api-loader";

import LottieLayout from "./Layouts/Lottie";
import ImageLayout from "./Layouts/Image";
import VimeoLayout from "./Layouts/Vimeo";
import KinescopeLayout from "./Layouts/Kinescope";
import ProjectTypes from "./ProjectTypes";

const ProjectSimpleCard = memo(({
  project,
}) => {
  const { t } = useTranslation();
  const currentLayout = project.layouts[0];
  const playerRef = useRef();

  useEffect(() => {
    if (currentLayout.layout_type_codename !== "video") return;

    if (/https?:\/\/vimeo\.com/.test(currentLayout.video_url)) {
      handleSwitchToVimeoPlayer();
    } else if (/https?:\/\/kinescope\.io/.test(currentLayout.video_url)) {
      handleSwitchToKinescopePlayer();
    }
  }, [currentLayout]);

  const handleSwitchToVimeoPlayer = () => {
    if (!playerRef.current) {
      playerRef.current = new VimeoPlayer(`vimeo-${currentLayout.id}`);
      playerRef.current.ready().then(() => {
        if (currentLayout.play_mode === "loop")
          playerRef.current.play();
      });
    } else {
      if (currentLayout.play_mode === "loop")
        playerRef.current.play();
    }
  };

  const handleSwitchToKinescopePlayer = async () => {
    const isLoop = currentLayout.play_mode === "loop";

    if (!playerRef.current) {
      const isLoop = layout.play_mode === "loop";
      const factory = await iframeApiLoader.load();
      playerRef.current = await factory.create(
        `kinescope-${currentLayout.id}`,
        {
          url: layout.video_url,
          behavior: {
            loop: isLoop,
            muted: isLoop,
          },
          ui: {
            controls: !isLoop,
          }
        }
      );
    }

    if (isLoop)
      playerRef.current.play();
  };

  return (
    <div className="project-gallery">
      <div className="project-gallery__viewport-and-info">
        <div className="project-gallery__viewport">
          <div
            key={currentLayout.id}
            className={classNames("project-gallery__layout", "project-gallery__layout--show")}
          >
            {currentLayout.layout_type_codename === "image" && (
              <ImageLayout key={currentLayout.id} layout={currentLayout} />
            )}

            {currentLayout.layout_type_codename === "video" && /https?:\/\/vimeo\.com/.test(currentLayout.video_url) && (
              <VimeoLayout key={currentLayout.id} layout={currentLayout} />
            )}

            {currentLayout.layout_type_codename === "video" && /https?:\/\/kinescope\.io/.test(currentLayout.video_url) && (
              <KinescopeLayout key={layout.id} layout={layout} />
            )}

            {currentLayout.layout_type_codename === "lottie" && (
              <LottieLayout key={currentLayout.id} layout={currentLayout} />
            )}
          </div>
        </div>

        <div className="project-gallery__info">
          <div className="project-gallery__info__top">
            <h2><a href={project.url}>{project.title}</a></h2>

            <ProjectTypes project_types={project.project_types} />
          </div>

          <div className="project-gallery__info__text">
            <p>{currentLayout.description}</p>
            <div><a href={project.url} className="btn">{t('common.more')}</a></div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ProjectSimpleCard;
